import React from "react";
import { Bell } from "lucide-react";
import type { BroadcastFormState } from "src/types/broadcasts";
import { buildApiRequestBody } from "src/lib/broadcastService";
import { getBroadcastEnvInfo } from "src/lib/notificationRoutes";
import Collapsible from "../Collapsible";
import CopyButton from "../CopyButton";

interface Props {
  form: BroadcastFormState;
}

const TYPE_LABEL: Record<BroadcastFormState["type"], string> = {
  NEWS: "News",
  PROMOTIONAL: "Promo",
};

const TYPE_TONE: Record<BroadcastFormState["type"], string> = {
  NEWS: "bg-indigo-100 text-indigo-700 border-indigo-200",
  PROMOTIONAL: "bg-amber-100 text-amber-800 border-amber-200",
};

export const NotificationPreview: React.FC<Props> = ({ form }) => {
  const envInfo = getBroadcastEnvInfo(form.env);
  const body = buildApiRequestBody(form);
  const title = form.title.trim() || "Notification title";
  const message = form.body.trim() || "Your message text will appear here.";

  return (
    <div className="space-y-4 lg:sticky lg:top-6">
      <div className="rounded-[2rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:p-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-extrabold uppercase tracking-wide text-[#777777]">
            Preview
          </p>

          <span
            className={`rounded-md border px-2 py-0.5 text-xs font-bold ${TYPE_TONE[form.type]}`}
          >
            {TYPE_LABEL[form.type]}
          </span>
        </div>

        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-[#F4F4F5] p-4 shadow-[0_4px_0_#E5E5E5]">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-indigo-500 text-white shadow-sm">
              <Bell className="h-5 w-5" />
            </div>

            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="truncate text-sm font-extrabold text-[#1F1F1F]">
                  iVueIt
                </p>

                <span className="shrink-0 text-[10px] font-bold uppercase text-[#9CA3AF]">
                  now
                </span>
              </div>

              <p className="break-words text-sm font-bold text-[#1F1F1F]">
                {title}
              </p>

              <p className="mt-0.5 break-words text-sm text-[#4B5563]">
                {message}
              </p>
            </div>
          </div>
        </div>

        <p className="mt-3 text-xs text-[#777777]">
          Sends to{" "}
          <span className="font-bold text-[#1F1F1F]">{envInfo.label}</span> (
          <span className="font-mono">{envInfo.baseUrl}</span>).
        </p>
      </div>

      <Collapsible
        title="API payload"
        defaultOpen
        className="rounded-[1.5rem] border-4 shadow-[0_8px_0_#D0D0D0]"
        headerClassName="px-5 py-1"
        contentClassName="p-0"
      >
        <div className="mb-2 flex items-center justify-end">
          <CopyButton value={JSON.stringify(body, null, 2)} />
        </div>
        <pre className="w-full max-w-full overflow-x-auto whitespace-pre-wrap break-all rounded-b-[1.25rem] bg-[#0F172A] p-4 font-mono text-xs leading-relaxed text-emerald-100">
          {JSON.stringify(body, null, 2)}
        </pre>
      </Collapsible>
    </div>
  );
};

export default NotificationPreview;
